import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import {
  getFiduciary,
  getGoPeopleSelectedCommunity,
  getOrganizationSettings,
  getUaSettingByOrganisation,
  checkUaAccess,
} from "../api";
import {
  getCommunityFromCookie,
  transformFidToOrganization,
  getAuthInfosFromCookie,
  extractSelectedCommunityOrgs,
  updateAuthCookieGoPeopleOrganizationId,
} from "../utils";

import {
  unsetNavCommunity,
  setAuthGoPeopleOrganizationId,
  setAuthGoPeopleCommunityId,
} from "./auth";

const initialState = {
  fetching: false,
  fetched: false,
  fiduciary: null,
  freeCycle: false,
};

export const fetchFiduciary = createAsyncThunk(
  "folder/fetchFiduciary",
  async (tmp, { getState, dispatch }) => {
    let { token, user, goPeopleOrganizationId } = getState().auth;
    if (!token) {
      return null;
    }
    if (!user) {
      await new Promise((r) => setTimeout(r, 1000));
      user = getState().auth.user;
    }
    const state = getState();

    try {
      const community = getCommunityFromCookie();
      const cookie = getAuthInfosFromCookie();
      let fiduciaire;
      if (goPeopleOrganizationId || (cookie && cookie.goPeopleOrganizationId && cookie.jwtToken)) {
        // The full organization detail is already embedded in the
        // selected-community response (organizations[]), so we read it from
        // there instead of a dedicated GET /organizations/{id}. This endpoint
        // is authoritative and always fresh, so refreshes still pick up
        // ua_status / ua_mode / ua_plan changes.
        const targetOrgUuid = tmp
          ? tmp
          : goPeopleOrganizationId
            ? goPeopleOrganizationId
            : cookie.goPeopleOrganizationId;

        const selectedCommunityInfo = await getGoPeopleSelectedCommunity();
        const orgs = extractSelectedCommunityOrgs(
          selectedCommunityInfo,
          selectedCommunityInfo?.uuid
        );
        const fid =
          orgs.find((o) => o.uuid === targetOrgUuid) || orgs[0] || null;

        if (!fid) {
          dispatch(unsetNavCommunity());
          return null;
        }

        // Reconcile the selected community/org ids with the authoritative
        // selected-community response. On reload these ids are rehydrated from
        // this browser's cookie, which can be stale if the selection changed
        // elsewhere (another browser/tab). Without this, org-scoped lists
        // (collaborators, clients) keep querying the previous organization even
        // though the community/org shown is already the new one.
        const resolvedCommunityUuid = selectedCommunityInfo?.uuid;
        if (fid.uuid && fid.uuid !== goPeopleOrganizationId) {
          dispatch(setAuthGoPeopleOrganizationId(fid.uuid));
        }
        if (resolvedCommunityUuid) {
          dispatch(setAuthGoPeopleCommunityId(resolvedCommunityUuid));
        }
        updateAuthCookieGoPeopleOrganizationId(
          null,
          fid.uuid,
          resolvedCommunityUuid,
          selectedCommunityInfo?.ttp_community_id ?? null,
          false
        );

        let organisationSettings = {};
        let uaSettings = {};
        let ttpUaHasAccess = null;
        let ttpOrganisationSettingsResponse = null;
        let ttpUaSettingsResponse = null;


        try {
          ttpOrganisationSettingsResponse = await getOrganizationSettings(
            token,
            fid?.ttp_organization_id ?? null
          );
          organisationSettings = ttpOrganisationSettingsResponse?.data?.data ?? {};
        } catch (e) {
          console.warn("Organization settings failed:====..", e);
        }

        try {
          ttpUaSettingsResponse = await getUaSettingByOrganisation(
            token,
            fid?.ua_settings_id ?? null
          );
          uaSettings = ttpUaSettingsResponse?.data?.data ?? {};
        } catch (e) {
          console.warn("UA settings failed:=====..", e);
        }

        if (fid?.ttp_organization_id) {
          let respAccess = await checkUaAccess(token, fid.ttp_organization_id);
          ttpUaHasAccess = respAccess.data.data?.uaHasAccess ?? null;
        }

        fiduciaire = transformFidToOrganization(
          fid,
          ttpOrganisationSettingsResponse.data.data,
          ttpUaSettingsResponse.data.data,
          ttpUaHasAccess
        );

        return fiduciaire;
      }

      const response = await getFiduciary({
        token,
        fiduciaryId: community
          ? community.id
          : cookie.communityApiId
            ? cookie.communityApiId
            : null,
        user: user ?? null,
      });

      if (response.data.data.length === 1) {
        return response.data.data[0];
      } else {
        let folder = null;
        response.data.data.forEach((item) => {
          if (!folder || folder?.uaStatus !== "VALIDATED") {
            if (!folder) {
              folder = item;
            } else {
              if (item.uaStatus === "VALIDATED") {
                folder = item;
              } else if (
                item.uaStatus === "PENDING" &&
                folder.uaStatus === "CREATED"
              ) {
                folder = item;
              }
            }
          }
        });

        return folder;
      }
    } catch (e) {
      console.log("fetch ficuiareee error ==", e);
      dispatch(unsetNavCommunity());
      return null;
    }
  }
);

const folderSlice = createSlice({
  name: "folder",
  initialState,
  reducers: {
    setFreeCycle: (state, { payload }) => {
      state.freeCycle = payload;
    },
    setFiduciary: (state, { payload }) => {
      state.fiduciary = payload;
    },
    setFolderFetched: (state, { payload }) => {
      state.fetched = payload;
    },
    setOnBoardingStep: (state, { payload }) => {
      if (state.fiduciary) {
        state.fiduciary.onBoardingStep = payload;
      }
    },
    setPortalSubDomain: (state, { payload }) => {
      if (state.fiduciary) {
        state.fiduciary.subdomain = payload;
      }
    },
    setHubPreferencesPortals: (state, { payload }) => {
      if (state.fiduciary) {
        if (state.fiduciary.hubPreferences) {
          state.fiduciary.hubPreferences = {
            ...state.fiduciary.hubPreferences,
            portals: payload,
          };
        } else {
          state.fiduciary.hubPreferences = { portals: payload };
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchFiduciary.pending, (state, action) => {
      state.fetching = true;
      state.fetched = false;
    });
    builder.addCase(fetchFiduciary.fulfilled, (state, action) => {
      state.fetched = true;
      state.fetching = false;
      if (action.payload) {
        state.fiduciary = action.payload;
      } else {
        state.fiduciary = null;
      }
    });
    builder.addCase(fetchFiduciary.rejected, (state, action) => {
      state.fiduciary = null;
      state.fetched = true;
      state.fetching = false;
    });
  },
});
export const {
  setFreeCycle,
  setFiduciary,
  setFolderFetched,
  setOnBoardingStep,
  setPortalSubDomain,
  setHubPreferencesPortals,
} = folderSlice.actions;

export default folderSlice.reducer;
